import {
  createMemoryHistory,
  createRouter,
  createWebHistory
} from 'vue-router';
import { getStore } from '@core/store';
import Commons from '../../features/shared/libraries/commons';

let initialUrl = null;
class AppRouter {
  constructor() {
    this.routeModules = this.loadRouteModules();
    this.routes = this.extractRoutes(this.routeModules);
    this.router = this.createVueRouter(this.routes);
    this.setupGuards();
    this.findInitialUrl();
  }

  loadRouteModules() {
    return import.meta.glob('/src/app/features/**/routes.js', { eager: true });
  }

  extractRoutes(modules) {
    if (!modules) return [];
    return Object.values(modules).flatMap((mod) => mod?.default || mod);
  }

  createVueRouter(routes) {
    return createRouter({
      history: createWebHistory(),
      routes: routes
      // Wildcard redirect retiré - causait une boucle infinie
    });
  }

  setupGuards() {
    this.router.beforeEach(this.handleNavigation.bind(this));
  }

  handleNavigation(destination, origin, next) {
    this.setPageHeaderColor(destination);
    // this.setRoutePath(destination, origin); // Désactivé - cause une boucle infinie avec vue-router

    // Admin security: check domain for admin routes
    const adminRouteCheck = this.checkAdminRouteSecurity(destination);
    if (adminRouteCheck !== 'proceed') {
      next(adminRouteCheck);
      return;
    }

    if (!this.requiresAuth(destination)) {
      next();
      return;
    }

    if (this.isUserLoggedIn()) {
      next();
    } else {
      next('/login');
    }
  }

  setPageHeaderColor(destination) {
    try {
      Commons.setThemeColor(destination?.meta?.headerColor);
    } catch (error) {
      console.log('🚀 ~ AppRouter ~ setPageHeaderColor ~ error:', error);
    }
  }

  checkAdminRouteSecurity(destination) {
    try {
      const hostname = window.location?.hostname || '';
      const path = destination?.path || '';

      // Check if current route is an admin route
      const isAdminRoute = path.startsWith('/admin');

      // Check if current domain is admin domain
      const isAdminDomain = hostname.includes('admin');

      // 1. Redirect admin domain non-admin routes to admin dashboard
      if (isAdminDomain && !isAdminRoute) {
        console.log('🔒 Admin security: redirecting admin domain to /admin/dashboard');
        return '/admin/dashboard';
      }

      // 2. Redirect non-admin domain admin routes to home
      if (isAdminRoute && !isAdminDomain && !this.isLocalhost()) {
        console.log('🔒 Admin security: blocking admin access from non-admin domain');
        return '/';
      }

      return 'proceed';
    } catch (error) {
      console.log('🚀 ~ AppRouter ~ checkAdminRouteSecurity ~ error:', error);
      return 'proceed';
    }
  }

  isLocalhost() {
    const hostname = window.location?.hostname || '';
    return hostname === 'localhost' ||
           hostname === '127.0.0.1' ||
           hostname.startsWith('192.168.') ||
           hostname.startsWith('10.') ||
           hostname.startsWith('172.');
  }

  setRoutePath(destination, origin) {
    try {
      const currentPath =
        window.location.pathname +
        window.location.search +
        window.location.hash;
      if (window.programaticBackCalled) {
        window.programaticBackCalled = false;
        window.history.replaceState(
          { date: Date.now() },
          null,
          destination.fullPath
        );
        console.log('replaced from route');

        return;
      }
      if (currentPath == destination.fullPath) return;
      window.history.pushState(
        { date: Date.now() },
        null,
        destination.fullPath
      );
      console.log('pushed from route');
    } catch (error) {
      console.log('🚀 ~ AppRouter ~ setRoutePath ~ error:', error);
    }
  }

  requiresAuth(destination) {
    return Boolean(destination?.meta?.requiresAuth);
  }

  isUserLoggedIn() {
    const userStore = getStore('user');
    return Boolean(userStore?.state?.isLoggedIn);
  }
  findInitialUrl() {
    try {
      if (typeof window === 'undefined') return;
    } catch (error) {
      console.log('🚀 ~ AppRouter ~ findInitialUrl ~ error:', error);
      return;
    }

    initialUrl =
      window.location.pathname + window.location.search + window.location.hash;
  }

  static makeInitialNaviation() {
    try {
      if (initialUrl && typeof initialUrl === 'string') {
        window.$router.replace(initialUrl);
      } else {
        window.$router.replace('/');
      }
    } catch (error) {
      console.error('Initial navigation error:', error);
    }
  }

  get instance() {
    return this.router;
  }
}

function createAppRouter() {
  const appRouter = new AppRouter();
  const router = appRouter.instance;

  if (typeof window !== 'undefined') {
    window.$router = router;
  }

  return router;
}

export { AppRouter, createAppRouter };
